import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { AgencyMarginBreakdown } from '@/utils/estimates/computeAgencyMarginBreakdown';
import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import { useTranslation } from '@/i18n';
import clsx from 'clsx';

interface EstimateAgencyMarginsCardProps {
  breakdown: AgencyMarginBreakdown;
}

const ReadOnlyAmountField = ({
  label,
  amount,
}: {
  label: string;
  amount: number;
}) => (
  <div className="space-y-1.5">
    <Label className="text-gray-700">{label}</Label>
    <div
      className={clsx(
        'flex h-10 items-center rounded-lg border border-gray-300 bg-gray-100 px-3 text-base text-gray-700',
      )}
      aria-readonly
    >
      {formatPrice(amount)}
    </div>
  </div>
);

const EstimateAgencyMarginsCard = ({
  breakdown,
}: EstimateAgencyMarginsCardProps) => {
  const { t } = useTranslation();

  const hasGeneralMargin =
    breakdown.generalMargin != null && breakdown.generalMargin > 0;
  const hasAdditionalMargin =
    breakdown.additionalMargin != null && breakdown.additionalMargin > 0;
  const hasFlightsMargin =
    breakdown.flightsMargin != null && breakdown.flightsMargin > 0;

  if (breakdown.totalMargin == null || breakdown.totalMargin <= 0) {
    return null;
  }

  const primaryLabel = hasGeneralMargin
    ? t('estimates.agencyView.marginsSummary.generalMargin')
    : hasAdditionalMargin
      ? t('estimates.agencyView.marginsSummary.additionalMargin')
      : null;
  const primaryAmount = hasGeneralMargin
    ? breakdown.generalMargin
    : breakdown.additionalMargin;

  const showSecondaryAdditional = hasGeneralMargin && hasAdditionalMargin;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h1 className="text-xl font-medium text-black">
            {t('estimates.agencyView.marginsSummary.title')}
          </h1>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {primaryLabel != null && primaryAmount != null && (
          <div className="grid gap-4 md:grid-cols-2 md:gap-x-4">
            <ReadOnlyAmountField
              label={primaryLabel}
              amount={primaryAmount}
            />
            {showSecondaryAdditional && breakdown.additionalMargin != null && (
              <ReadOnlyAmountField
                label={t(
                  'estimates.agencyView.marginsSummary.additionalMargin',
                )}
                amount={breakdown.additionalMargin}
              />
            )}
          </div>
        )}
        {hasFlightsMargin && breakdown.flightsMargin != null && (
          <div className="grid gap-4 md:grid-cols-2 md:gap-x-4">
            <ReadOnlyAmountField
              label={t('estimates.agencyView.marginsSummary.flightsMargin')}
              amount={breakdown.flightsMargin}
            />
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-2 md:gap-x-4">
          <ReadOnlyAmountField
            label={t('estimates.agencyView.marginsSummary.totalMargin')}
            amount={breakdown.totalMargin}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default EstimateAgencyMarginsCard;
